/**
 * Every request to our own API carries the signed-in user's Clerk session
 * token (2026-10-05).
 *
 * The backend used to believe whatever X-User-ID a request carried, and its
 * admin routes answered anyone (backend/internal/api/rest/auth_guard.go). The
 * server-side gates verify this token instead. Many panels call fetch()
 * directly rather than through api/client.ts, so the token is added here, once,
 * for every same-origin /api request: a panel written tomorrow is covered
 * without anyone remembering to.
 *
 * Never sent anywhere else, and never overwrites an Authorization header a
 * caller set on purpose (the MCP and tensor tokens, the admin poll trigger).
 * With no Clerk session (signed out, demo) the request goes as before.
 */

/** Whether a request URL is our own API. Pure, so it can be tested. */
export function isOwnApi(url: string, origin: string, apiBase: string): boolean {
  if (url.startsWith('/api/')) return true;
  for (const base of [apiBase, origin]) {
    if (base && url.startsWith(base.replace(/\/$/, '') + '/api/')) return true;
  }
  return false;
}

type ClerkLike = { session?: { getToken?: () => Promise<string | null> } };

export function installAuthFetch(apiBase: string): void {
  const w = window as unknown as { fetch: typeof fetch; Clerk?: ClerkLike; __copyfiAuthFetch?: boolean };
  if (w.__copyfiAuthFetch) return;
  w.__copyfiAuthFetch = true;
  const original = w.fetch.bind(window);

  w.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    if (!isOwnApi(url, window.location.origin, apiBase)) return original(input, init);

    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (!headers.has('Authorization')) {
      let token: string | null = null;
      try {
        token = (await w.Clerk?.session?.getToken?.()) ?? null;
      } catch {
        token = null;
      }
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }
    if (input instanceof Request) return original(new Request(input, { ...init, headers }));
    return original(input, { ...init, headers });
  };
}
